import { useEffect, useState } from 'react';
import { encodeFunctionData } from 'viem';
import { ethers } from 'ethers';

import { abiChestNFT, abiChestFactory, abiVRFCoordinator } from '../config/abi.contract';
import useWrappedRPC from './useWrappedRPC';
import {
  getChestMintSignature,
  validateOpenChestTransaction,
  validateWithdrawChestTransaction,
} from '../services/transaction.service';
import { getLuckyChestConfig } from '../services/chest.service';
import useToken from './useToken';
import { dispatchGameEvent } from '../utils/common';
import { provider } from '../config/provider.config';
import environments from '../utils/environments';
import useUserStore from '../stores/user.store';

const chestFactoryInterface = new ethers.utils.Interface(abiChestFactory);

const { CHEST_NFT_CONTRACT_ADDRESS, CHEST_FACTORY_CONTRACT_ADDRESS, VRF_COORDINATOR_CONTRACT_ADDRESS } = environments;

function useChest() {
  const { getFISHTokenBalance } = useToken();
  const { sendTransactionWithGas, provider } = useWrappedRPC();
  const user = useUserStore((state) => state.user);

  // State
  const [luckyChestConfig, setLuckyChestConfig] = useState(null);

  // Contracts
  const erc721Contract = new ethers.Contract(CHEST_NFT_CONTRACT_ADDRESS, abiChestNFT, provider);
  const chestFactoryContract = new ethers.Contract(CHEST_FACTORY_CONTRACT_ADDRESS, abiChestFactory, provider);

  // ---- Fetch Lucky Chest Configuration ---- //
  useEffect(() => {
    const fetchLuckyChestConfig = async () => {
      try {
        const response = await getLuckyChestConfig();
        console.log('✅ Lucky chest config loaded:', response.data);
        setLuckyChestConfig(response.data.config);
      } catch (error) {
        console.error('❌ Error fetching lucky chest config:', error);
      } finally {
      }
    };

    fetchLuckyChestConfig();
  }, []);

  // ---- Get Lucky Chest Rewards by Request Hash ---- //
  const getLuckyChestRewardsByReqHash = async ({ requestHash, txnHash }, maxRetries = MAX_RETRIES) => {
    console.log(`[getLuckyChestRewardsByReqHash] txnHash=${txnHash}`);
    let attempts = 0;

    while (attempts < maxRetries) {
      attempts++;

      try {
        const result = await chestFactoryContract.requestContexts(requestHash);

        console.log(`[getLuckyChestRewardsByReqHash] result`, result);

        if (result?.generated) {
          console.log('✅ Lucky chest reward found:', {
            user: result.user,
            amount: parseFloat(ethers.utils.formatEther(result.amount)),
            requestHash,
          });

          return {
            ...result,
            amount: parseFloat(ethers.utils.formatEther(result.amount)),
            fromVRF: false,
          };
        }
      } catch (error) {
        console.error(`❌ Error on attempt ${attempts}:`, error.message);
      }

      // Wait before retry
      if (attempts < maxRetries) {
        console.log(`⏳ Attempt ${attempts}/${maxRetries} - Retrying in ${RETRY_DELAY_MS}ms...`);
        await delay(RETRY_DELAY_MS);
      }
    }

    throw new Error('Timeout waiting for lucky chest rewards. Please check back later or contact support.');
  };

  // ---- Ensure NFT Approval for Chest Factory ---- //
  const ensureChestApproval = async (userAddress) => {
    const isApproved = await erc721Contract.isApprovedForAll(userAddress, CHEST_FACTORY_CONTRACT_ADDRESS);

    if (isApproved) {
      console.log('✅ Chest approval already granted');
      return;
    }

    console.log('🔐 Requesting chest approval...');

    const approvalData = encodeFunctionData({
      abi: abiChestNFT,
      functionName: 'setApprovalForAll',
      args: [CHEST_FACTORY_CONTRACT_ADDRESS, true],
    });

    await sendTransactionWithGas(
      {
        to: CHEST_NFT_CONTRACT_ADDRESS,
        data: approvalData,
      },
      {
        header: 'Approve all chests',
        description: 'Approve all chests for Fishing Game',
        buttonText: 'Approve all',
      }
    );

    console.log('✅ Chest approval granted');
  };

  // ---- Open Lucky Chest ---- //
  const openLuckyChest = async (tokenId, callback) => {
    try {
      console.log('[openLuckyChest]', { tokenId });

      // Ensure approval
      await ensureChestApproval(user.walletAddress);

      console.log(`[openLuckyChest] approval done`);

      // Get VRF fee
      const { callbackGasLimit, gasPriceToFulfill, msgValue } = await calculateVRFFee();

      console.log(`[openLuckyChest] calculateVRFFee`, { callbackGasLimit, gasPriceToFulfill, msgValue });

      // Encode transaction data
      const data = encodeFunctionData({
        abi: abiChestFactory,
        functionName: 'openLuckyChest',
        args: [tokenId, callbackGasLimit, gasPriceToFulfill],
      });

      const txUiConfig = {
        header: 'Open Lucky Chest',
        description: `Open chest with ID ${tokenId}`,
        buttonText: 'Open',
      };

      const transactionRequest = {
        to: CHEST_FACTORY_CONTRACT_ADDRESS,
        value: msgValue,
        data,
      };

      // Send transaction
      const { txReceipt, receipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);

      if (!txReceipt?.hash) {
        throw new Error('Transaction failed on blockchain');
      }

      console.log('✅ Transaction confirmed:', txReceipt.hash);

      // Notify game to refresh inventory
      dispatchGameEvent({
        type: 'refreshInventoryChest',
        hasWaiting: false,
        notifyMessage: null,
      });

      if (callback) callback();

      // Get transaction receipt and extract request ID
      const eventSignature = chestFactoryInterface.getEventTopic('LuckyChestOpenRequested');
      const log = receipt.logs.find((l) => l.topics[0] === eventSignature);
      const parsedLog = chestFactoryInterface.parseLog(log);

      // RequestId is the 2nd indexed parameter of the RandomNumberRequested event (3rd log)
      const requestId = parsedLog.args.requestId;

      if (!requestId) {
        throw new Error('Could not extract request ID from transaction receipt');
      }

      console.log('🎲 Request ID:', requestId);

      // Wait for and get rewards
      const rewardResult = await getLuckyChestRewardsByReqHash({
        requestHash: requestId,
        txnHash: txReceipt.hash,
      });

      console.log('💰 Reward amount:', rewardResult.amount, 'FISH');

      // Format reward data with animation config
      const rewardsData = formatRewardData(rewardResult.amount, luckyChestConfig);

      // Get updated balance
      const updatedBalance = await getFISHTokenBalance();
      console.log('💵 Updated FISH balance:', updatedBalance.toLocaleString());

      // Final inventory refresh
      dispatchGameEvent({
        type: 'refreshInventoryChest',
        hasWaiting: false,
        notifyMessage: null,
      });

      return {
        success: true,
        message: 'Lucky chest opened successfully',
        data: {
          ...rewardsData,
          fishTokenBalance: updatedBalance,
          fromVRF: rewardResult.fromVRF,
        },
      };
    } catch (error) {
      console.error('❌ Open lucky chest failed:', error);
      return {
        success: false,
        message: error.message || 'Failed to open lucky chest',
      };
    }
  };

  // ---- Open Batch NFT Chests ---- //
  const openBatchNFTChest = async (userChestTokenIds, callback) => {
    try {
      console.log('🎁 Opening batch chests:', userChestTokenIds);

      // Ensure approval
      await ensureChestApproval(user.walletAddress);

      // Encode transaction data
      const data = encodeFunctionData({
        abi: abiChestFactory,
        functionName: 'openBatchChest',
        args: [userChestTokenIds],
      });

      const txUiConfig = {
        header: 'Open Chests',
        description: `Open chests with IDs ${userChestTokenIds.join(', ')}`,
        buttonText: 'Open',
      };

      const transactionRequest = {
        to: CHEST_FACTORY_CONTRACT_ADDRESS,
        data,
      };

      // Send transaction
      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);

      if (!txReceipt?.hash) {
        throw new Error('Transaction failed on blockchain');
      }

      console.log('✅ Batch open transaction confirmed:', txReceipt.hash);

      if (callback) callback();

      // Validate with backend
      const backendResponse = await validateOpenChestTransaction({
        transactionHash: txReceipt.hash,
      });

      return {
        success: true,
        message: 'Chests opened successfully',
        data: backendResponse.data,
      };
    } catch (error) {
      console.error('❌ Open batch chests failed:', error);
      return {
        success: false,
        message: error.message || 'Failed to open chests',
      };
    }
  };

  // ---- Withdraw/Mint Chests ---- //
  const withdrawChest = async (userChestTokenIds) => {
    try {
      console.log('📤 Withdrawing chests:', userChestTokenIds);

      // Get signature from backend
      const signatureRes = await getChestMintSignature({ userChestTokenIds });
      const { chestTokenIds, tierIds, quantities, nonce, deadline, signature } = signatureRes.data;

      // Encode transaction data
      const data = encodeFunctionData({
        abi: abiChestFactory,
        functionName: 'mintBatchChestWithSignature',
        args: [chestTokenIds, tierIds, quantities, nonce, deadline, signature],
      });

      const txUiConfig = {
        header: 'Withdraw Chest',
        description: 'Withdraw Chest',
        buttonText: 'Withdraw',
      };

      const transactionRequest = {
        to: CHEST_FACTORY_CONTRACT_ADDRESS,
        data,
      };

      // Send transaction
      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);

      if (!txReceipt?.hash) {
        throw new Error('Transaction failed on blockchain');
      }

      console.log('✅ Withdraw transaction confirmed:', txReceipt.hash);

      // Validate with backend
      const backendResponse = await validateWithdrawChestTransaction({
        transactionHash: txReceipt.hash,
      });

      return {
        success: true,
        message: 'Chests withdrawn successfully',
        data: backendResponse.data,
      };
    } catch (error) {
      console.error('❌ Withdraw chests failed:', error);
      return {
        success: false,
        message: error.message || 'Failed to withdraw chests',
      };
    }
  };

  return {
    openLuckyChest,
    openBatchNFTChest,
    withdrawChest,
  };
}

export default useChest;

export const calculateVRFFee = async () => {
  const block = await provider.getBlock('latest');
  const gasPriceToFulfill = block.baseFeePerGas.mul(2).add(ethers.utils.parseUnits('20', 'gwei'));

  const callbackGasLimit = 300_000;

  const vrfCoordinatorAddress = new ethers.Contract(VRF_COORDINATOR_CONTRACT_ADDRESS, abiVRFCoordinator, provider);
  const estimatedRandomFee = await vrfCoordinatorAddress.estimateRequestRandomFee(callbackGasLimit, gasPriceToFulfill);

  console.log({
    gasPriceToFulfill: gasPriceToFulfill.toString(),
    callbackGasLimit: callbackGasLimit.toString(),
    estimatedRandomFee: estimatedRandomFee.toString(),
  });

  return {
    callbackGasLimit,
    gasPriceToFulfill,
    msgValue: estimatedRandomFee,
  };
};

// ---- Constants ---- //
const MAX_RETRIES = 30; // 30 seconds with 1s delay
const RETRY_DELAY_MS = 1000;
const BASIS_POINTS_DIVISOR = 10000; // 100.00% = 10000 basis points

// ---- Helper functions ---- //
export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Format reward data for display with animation config
 * @param {number} amount - Reward amount
 * @param {Object} luckyChestConfig - Configuration from backend
 * @returns {Object} Formatted reward data
 */
const formatRewardData = (amount, luckyChestConfig) => {
  const defaultReward = {
    imageName: 'fish_token',
    name: 'FISH',
    type: 'FISH Token',
    description: 'Token that can be used for in-game utility or exchanged for other tokens.',
    quantity: amount,
    quality: 1,
    order: 1,
    animation: 1,
  };

  if (!luckyChestConfig?.lootItems || amount === 0) {
    return { totalItems: [defaultReward] };
  }

  const matchingReward = luckyChestConfig.lootItems[0].find((item) => item.quantity === amount);

  return {
    totalItems: [matchingReward || defaultReward],
  };
};
